import { useEffect, useRef, useState } from 'react';
import { Loader2, Box } from 'lucide-react';

interface ModelThumbnail3DProps {
  modelUrl: string;
  className?: string;
}

type RGB = [number, number, number];

interface ColorPalette {
  dominant: RGB;
  palette: RGB[];
}

// Simple color quantization to extract dominant colors
function extractColors(imageData: ImageData, colorCount = 5): ColorPalette | null {
  const pixels: RGB[] = [];
  const data = imageData.data;

  // Sample pixels (skip transparent ones)
  for (let i = 0; i < data.length; i += 16) { // Sample every 4th pixel for performance
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    if (a > 128) { // Skip transparent
      pixels.push([r, g, b]);
    }
  }

  if (pixels.length === 0) return null;

  // Simple k-means-like clustering
  const clusters: { center: RGB; pixels: RGB[] }[] = [];

  // Initialize clusters with random pixels
  for (let i = 0; i < colorCount; i++) {
    const randomPixel = pixels[Math.floor(Math.random() * pixels.length)];
    clusters.push({ center: [...randomPixel], pixels: [] });
  }

  // Run clustering iterations
  for (let iter = 0; iter < 10; iter++) {
    // Clear pixels from clusters
    clusters.forEach(c => c.pixels = []);

    // Assign pixels to nearest cluster
    for (const pixel of pixels) {
      let minDist = Infinity;
      let nearestCluster = clusters[0];

      for (const cluster of clusters) {
        const dist = Math.sqrt(
          Math.pow(pixel[0] - cluster.center[0], 2) +
          Math.pow(pixel[1] - cluster.center[1], 2) +
          Math.pow(pixel[2] - cluster.center[2], 2)
        );
        if (dist < minDist) {
          minDist = dist;
          nearestCluster = cluster;
        }
      }

      nearestCluster.pixels.push(pixel);
    }

    // Update cluster centers
    for (const cluster of clusters) {
      if (cluster.pixels.length > 0) {
        const sum = cluster.pixels.reduce(
          (acc, p) => [acc[0] + p[0], acc[1] + p[1], acc[2] + p[2]] as RGB,
          [0, 0, 0] as RGB
        );
        cluster.center = [
          Math.round(sum[0] / cluster.pixels.length),
          Math.round(sum[1] / cluster.pixels.length),
          Math.round(sum[2] / cluster.pixels.length),
        ];
      }
    }
  }

  // Sort clusters by pixel count (most common first)
  clusters.sort((a, b) => b.pixels.length - a.pixels.length);

  const palette = clusters
    .filter(c => c.pixels.length > 0)
    .map(c => c.center);

  return {
    dominant: palette[0] || [40, 40, 60],
    palette: palette.slice(0, colorCount),
  };
}

export default function ModelThumbnail3D({ modelUrl, className = '' }: ModelThumbnail3DProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<BABYLON.Engine | null>(null);
  const isMountedRef = useRef(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [colors, setColors] = useState<ColorPalette | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    if (!canvasRef.current) return;

    const BABYLON = (window as unknown as { BABYLON?: typeof import('babylonjs') }).BABYLON;
    if (!BABYLON) {
      setError(true);
      setIsLoading(false);
      return;
    }

    const canvas = canvasRef.current;
    let engine: BABYLON.Engine | null = null;
    let scene: BABYLON.Scene | null = null;
    let disposed = false;

    // Safe state setter
    const safeSetState = <T,>(setter: React.Dispatch<React.SetStateAction<T>>, value: T) => {
      if (isMountedRef.current && !disposed) {
        setter(value);
      }
    };

    // Extract colors from texture with validation
    const extractColorsFromTexture = async (texture: BABYLON.BaseTexture): Promise<ColorPalette | null> => {
      return new Promise((resolve) => {
        try {
          if (disposed || !isMountedRef.current) {
            resolve(null);
            return;
          }

          const size = texture.getSize();
          if (!size.width || !size.height || size.width <= 0 || size.height <= 0) {
            resolve(null);
            return;
          }

          // Read pixels from BabylonJS texture
          let pixels: Uint8Array | null = null;
          try {
            pixels = texture.readPixels() as Uint8Array | null;
          } catch {
            resolve(null);
            return;
          }

          if (!pixels || pixels.length === 0) {
            resolve(null);
            return;
          }

          // Validate data length matches expected size
          const expectedLength = size.width * size.height * 4;
          if (pixels.length !== expectedLength) {
            resolve(null);
            return;
          }

          // Create ImageData for color extraction
          const imageData = new ImageData(
            new Uint8ClampedArray(pixels),
            size.width,
            size.height
          );

          const extractedColors = extractColors(imageData, 5);
          resolve(extractedColors);
        } catch {
          resolve(null);
        }
      });
    };

    const init = async () => {
      try {
        // Create lightweight engine
        engine = new BABYLON.Engine(canvas, true, {
          preserveDrawingBuffer: false,
          stencil: false,
          alpha: false,
          antialias: true,
          powerPreference: 'low-power',
        });
        engineRef.current = engine;

        if (disposed) return;

        scene = new BABYLON.Scene(engine);
        scene.clearColor = new BABYLON.Color4(0.06, 0.06, 0.08, 1);

        // Static camera
        const camera = new BABYLON.ArcRotateCamera(
          'camera',
          Math.PI * 1.52,
          Math.PI / 3,
          4,
          BABYLON.Vector3.Zero(),
          scene
        );
        camera.attachControl(canvas, false);

        // Lighting
        const hemiLight = new BABYLON.HemisphericLight(
          'hemi',
          new BABYLON.Vector3(0, 1, 0),
          scene
        );
        hemiLight.intensity = 0.7;

        const dirLight = new BABYLON.DirectionalLight(
          'dir',
          new BABYLON.Vector3(-0.5, -1, 0.5),
          scene
        );
        dirLight.intensity = 0.6;

        scene.ambientColor = new BABYLON.Color3(0.3, 0.3, 0.3);

        if (disposed) return;

        // Load model with timeout
        const lastSlash = modelUrl.lastIndexOf('/');
        const rootUrl = modelUrl.substring(0, lastSlash + 1);
        const fileName = modelUrl.substring(lastSlash + 1);

        let result;
        try {
          result = await BABYLON.SceneLoader.ImportMeshAsync('', rootUrl, fileName, scene);
        } catch (loadError) {
          // Model loading failed - show fallback
          if (!disposed && isMountedRef.current) {
            safeSetState(setError, true);
            safeSetState(setIsLoading, false);
          }
          return;
        }

        if (disposed || !isMountedRef.current) return;

        if (result.meshes.length > 0) {
          const root = result.meshes[0];

          // Extract colors from first valid texture (silently fail if none)
          let colorsExtracted = false;
          for (const mesh of result.meshes) {
            if (mesh.material && !colorsExtracted && !disposed) {
              const pbr = mesh.material as BABYLON.PBRMaterial;
              const std = mesh.material as BABYLON.StandardMaterial;

              const texture = pbr.albedoTexture || std.diffuseTexture;
              if (texture) {
                try {
                  await new Promise<void>((resolve) => {
                    if (disposed) {
                      resolve();
                      return;
                    }
                    if (texture.isReady()) {
                      resolve();
                    } else {
                      texture.onLoadObservable.addOnce(() => resolve());
                      setTimeout(resolve, 1500); // Shorter timeout
                    }
                  });

                  if (!disposed && isMountedRef.current) {
                    const extractedColors = await extractColorsFromTexture(texture);
                    if (extractedColors && isMountedRef.current) {
                      safeSetState(setColors, extractedColors);
                      colorsExtracted = true;
                    }
                  }
                } catch {
                  // Silently ignore color extraction errors
                }
              }
            }
          }

          if (disposed) return;

          // Calculate bounds
          let min = new BABYLON.Vector3(Infinity, Infinity, Infinity);
          let max = new BABYLON.Vector3(-Infinity, -Infinity, -Infinity);

          result.meshes.forEach((mesh) => {
            if (mesh.getBoundingInfo) {
              const boundingInfo = mesh.getBoundingInfo();
              min = BABYLON.Vector3.Minimize(min, boundingInfo.boundingBox.minimumWorld);
              max = BABYLON.Vector3.Maximize(max, boundingInfo.boundingBox.maximumWorld);
            }
          });

          const center = min.add(max).scale(0.5);
          const size = max.subtract(min);
          const maxDim = Math.max(size.x, size.y, size.z);

          // Scale and center
          const targetSize = 2.5;
          const scaleFactor = maxDim > 0 ? targetSize / maxDim : 1;
          root.scaling = new BABYLON.Vector3(scaleFactor, scaleFactor, scaleFactor);
          root.position = center.scale(-scaleFactor);

          // Ensure opaque materials
          result.meshes.forEach((mesh) => {
            if (mesh.material) {
              mesh.material.alpha = 1;
              mesh.material.backFaceCulling = true;
              const pbr = mesh.material as BABYLON.PBRMaterial;
              if (pbr.albedoTexture) {
                pbr.albedoTexture.hasAlpha = false;
              }
              if (pbr.transparencyMode !== undefined) {
                pbr.transparencyMode = BABYLON.PBRMaterial.PBRMATERIAL_OPAQUE;
              }
            }
          });

          // Adjust camera
          camera.radius = targetSize * 1.3;
          camera.target = BABYLON.Vector3.Zero();

          // Stop all animations
          if (result.animationGroups) {
            result.animationGroups.forEach(ag => ag.stop());
          }
        }

        if (disposed) return;

        // Render loop
        engine.runRenderLoop(() => {
          if (!disposed && scene) {
            scene.render();
          }
        });

        safeSetState(setIsLoading, false);
      } catch {
        safeSetState(setError, true);
        safeSetState(setIsLoading, false);
      }
    };

    init();

    return () => {
      disposed = true;
      isMountedRef.current = false;

      // Stop render loop first
      if (engineRef.current) {
        engineRef.current.stopRenderLoop();
      }

      // Small delay to ensure render loop has stopped
      setTimeout(() => {
        if (engineRef.current) {
          try {
            engineRef.current.dispose();
          } catch {
            // Ignore disposal errors
          }
          engineRef.current = null;
        }
      }, 50);
    };
  }, [modelUrl]);

  // Generate gradient background from color palette
  const getBackgroundStyle = (): React.CSSProperties => {
    if (!colors) {
      return {
        background: 'linear-gradient(135deg, rgb(15, 15, 25) 0%, rgb(25, 25, 40) 100%)',
      };
    }

    const { dominant, palette } = colors;

    // Darken colors for background
    const darken = (color: RGB, factor = 0.25): string => {
      return `rgb(${Math.floor(color[0] * factor)}, ${Math.floor(color[1] * factor)}, ${Math.floor(color[2] * factor)})`;
    };

    // Get secondary color from palette
    const secondary = palette.find(c =>
      Math.abs(c[0] - dominant[0]) > 30 ||
      Math.abs(c[1] - dominant[1]) > 30 ||
      Math.abs(c[2] - dominant[2]) > 30
    ) || palette[1] || dominant;

    return {
      background: `
        radial-gradient(ellipse at 20% 10%, ${darken(dominant, 0.5)} 0%, transparent 50%),
        radial-gradient(ellipse at 85% 90%, ${darken(secondary, 0.4)} 0%, transparent 50%),
        linear-gradient(145deg, ${darken(dominant, 0.12)} 0%, ${darken(secondary, 0.08)} 100%)
      `,
    };
  };

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-white/5 ${className}`}>
        <Box size={32} className="text-white/20" />
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={getBackgroundStyle()}
    >
      {/* Glow effect */}
      {colors && (
        <div
          className="absolute inset-0 opacity-20 blur-3xl"
          style={{
            background: `radial-gradient(circle at 50% 60%, rgb(${colors.dominant[0]}, ${colors.dominant[1]}, ${colors.dominant[2]}) 0%, transparent 60%)`,
          }}
        />
      )}

      <canvas
        ref={canvasRef}
        className="w-full h-full relative z-10"
        style={{ touchAction: 'none' }}
      />

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <Loader2 className="w-6 h-6 animate-spin text-white/50" />
        </div>
      )}
    </div>
  );
}
